import type { FaseOS, OrdemServico } from '../types/ordemServico';

export const parseDataHoraFase = (dataFase: string, horario: string): Date | null => {
  try {
    let horarioFormatado = horario.trim();
    if (!horarioFormatado.includes(':')) {
      if (horarioFormatado.length === 4) {
        horarioFormatado = `${horarioFormatado.substring(0, 2)}:${horarioFormatado.substring(2, 4)}`;
      } else {
        return null;
      }
    }

    let dateStr = dataFase;
    if (typeof dateStr === 'string') {
      dateStr = dateStr.split('T')[0];
    }

    const dataHora = new Date(`${dateStr}T${horarioFormatado}:00`);
    return Number.isNaN(dataHora.getTime()) ? null : dataHora;
  } catch {
    return null;
  }
};

export const getFaseAlertaStatus = (fase: FaseOS): 'proxima' | 'atrasada' | null => {
  if (fase.status_fase !== 'Pendente') return null;
  if (!fase.data_fase || !fase.horario) return null;

  const dataHoraFase = parseDataHoraFase(fase.data_fase, fase.horario);
  if (!dataHoraFase) return null;

  const diffMs = dataHoraFase.getTime() - Date.now();
  const diffHoras = diffMs / (1000 * 60 * 60);

  if (diffMs < 0) return 'atrasada';
  if (diffHoras <= 1 && diffHoras >= 0) return 'proxima';
  return null;
};

export const formatarTempoRestante = (fase: FaseOS): string => {
  if (!fase.data_fase || !fase.horario) return '';

  const dataHoraFase = parseDataHoraFase(fase.data_fase, fase.horario);
  if (!dataHoraFase) return '';

  const diffMs = dataHoraFase.getTime() - Date.now();

  if (diffMs < 0) {
    const minutosAtraso = Math.abs(Math.floor(diffMs / (1000 * 60)));
    const horasAtraso = Math.floor(minutosAtraso / 60);
    if (horasAtraso > 0) return `${horasAtraso}h ${minutosAtraso % 60}min atrasado`;
    return `${minutosAtraso}min atrasado`;
  }

  const minutosRestantes = Math.floor(diffMs / (1000 * 60));
  const horasRestantes = Math.floor(minutosRestantes / 60);
  if (horasRestantes > 0) return `em ${horasRestantes}h ${minutosRestantes % 60}min`;
  return `em ${minutosRestantes}min`;
};

export const getOSAlertas = (os: OrdemServico): {
  temAlerta: boolean;
  alertas: Array<{ fase: FaseOS; status: 'proxima' | 'atrasada' }>;
  temAtrasada: boolean;
} => {
  if (!os.fases?.length) {
    return { temAlerta: false, alertas: [], temAtrasada: false };
  }

  const alertas = os.fases
    .map((fase) => {
      const status = getFaseAlertaStatus(fase);
      return status ? { fase, status } : null;
    })
    .filter((alert): alert is { fase: FaseOS; status: 'proxima' | 'atrasada' } => alert !== null);

  return {
    temAlerta: alertas.length > 0,
    alertas,
    temAtrasada: alertas.some((a) => a.status === 'atrasada')
  };
};

export const getStatusColor = (status: string): string => {
  switch (status) {
    case 'Pendente':
      return 'bg-yellow-100 text-yellow-800';
    case 'Em Andamento':
    case 'Em Rota':
      return 'bg-blue-100 text-blue-800';
    case 'Concluída':
      return 'bg-green-100 text-green-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

export const getFaseIcon = (nomeFase: string): string => {
  switch (nomeFase) {
    case 'Remoção':
      return '🚑';
    case 'Clínica':
      return '🏥';
    case 'Velório':
      return '⚰️';
    case 'Sepultamento':
      return '🪦';
    default:
      return '📍';
  }
};
